import { useCallback, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  type EntityFormData,
  type EntityFormProps,
  EntityFormSchema,
  type EntitySchemaProps,
} from '@polyai/schemas';
import type { UpdateEntityPayload } from '@polyai/stream/commands/handlers/entities';
import {
  createEntity,
  updateEntity,
} from '@polyai/stream/store/reducers/entities/actions';
import {
  selectEntities,
  selectEntityById,
  selectEntityByName,
} from '@polyai/stream/store/reducers/entities/selectors';
import { EntityType } from '@polyai/stream/store/reducers/entities/types';
import { entityReferencesAdapter } from '@polyai/stream/store/reducers/entities/utils';
import type { Step } from '@polyai/stream/store/reducers/flows';
import Button from '@polyai/ui/components/atoms/Button';
import { CloseIcon } from '@polyai/ui/components/atoms/Icons';
import StickySidebar, {
  SidebarScrollableContent,
  SidebarStickyHeader,
} from '@polyai/ui/components/molecules/StickySidebar';
import { useSidebar } from '@polyai/ui/components/organisms/Sidebar';

import FormProvider, { FormSelect } from 'components/molecules/Form';
import { FormTextInput } from 'components/molecules/Form/FormTextInput';
import { ACTION_SIDEBAR_ID } from 'components/molecules/LexicalEditor/plugins/ActionPicker/constants';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import useHeaderOffset from 'hooks/useHeaderOffset';
import { PROJECT_RESOURCE } from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { generateEntityId } from 'lib/prettyUuid';
import { Z_INDEXES } from 'screens/FlowDetails/constants';
import { useEntitySidebar } from 'screens/FlowDetails/context/EntitySidebarContext';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import { DEFAULT_ENTITY_TYPE, ENTITY_TYPE_OPTIONS } from './constants';
import { AlphanumericForm } from './EntityForm/AlphanumericForm';
import { MultipleChoiceForm } from './EntityForm/MultipleChoiceForm';
import { NumberForm } from './EntityForm/NumberForm';
import { PhoneNumberForm } from './EntityForm/PhoneNumberForm';
import { TimeForm } from './EntityForm/TimeForm';
import * as Styled from './EntitySidebar.styled';
import { ENTITY_SIDEBAR_UI_COPY } from './EntitySidebar.uiCopy';
import { getDefaultEntityConfig, validateEntityConfig } from './utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  stepId?: string;
  defaultName?: string;
  entityId?: string;
  stepType: Step['type'] | undefined;
  readonly: boolean;
}

const EntitySidebar = ({
  isOpen,
  onClose,
  stepId,
  defaultName,
  entityId,
  stepType,
  readonly,
}: Props) => {
  const { flowId } = useProjectRoute<{ flowId: string }>();
  const serverDispatch = useServerDispatch();
  const { open: isMainNavOpen } = useSidebar();
  const headerOffset = useHeaderOffset();
  const toast = useToast();
  const { setEntityId, upsertRef } = useEntitySidebar();
  const { isEditable } = useViewMode({ resource: PROJECT_RESOURCE.FLOWS });

  const existingEntities = useAppSelector(state => selectEntities(state));
  const existingEntityByName = useAppSelector(state =>
    defaultName ? selectEntityByName(state, defaultName) : undefined,
  );

  const existingEntityById = useAppSelector(state =>
    entityId ? selectEntityById(state, entityId) : undefined,
  );
  const existingEntity = existingEntityByName || existingEntityById;

  const defaultValues: EntityFormData = useMemo(
    () => ({
      name: existingEntity?.name || defaultName || '',
      description: existingEntity?.description || '',
      type: existingEntity?.type || DEFAULT_ENTITY_TYPE,
      config:
        existingEntity?.config ||
        getDefaultEntityConfig(existingEntity?.type || DEFAULT_ENTITY_TYPE),
    }),
    [existingEntity, defaultName],
  );

  const finalSchema = EntityFormSchema.superRefine((values, ctx) => {
    const exists = existingEntities.find(
      entity => entity.name === values.name && entity.id !== existingEntity?.id,
    );
    if (exists) {
      ctx.addIssue({
        path: ['name'],
        code: z.ZodIssueCode.custom,
        message: 'Entity name is already taken',
      });
    }

    if (values.config) {
      validateEntityConfig(values.config, ctx);
    }
  }).transform(data => {
    if (data.config?.$case === EntityType.ENUM && data.config.value.options) {
      const filteredOptions = data.config.value.options.filter(
        (option: string) => option.trim() !== '',
      );

      data.config.value = {
        ...data.config.value,
        options: filteredOptions,
      };
    }

    return data;
  });

  const methods = useForm<EntityFormProps, unknown, EntitySchemaProps>({
    resolver: zodResolver(finalSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const handleCreateOrUpdateEntity = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    async (data: EntitySchemaProps) => {
      if (!isOpen) {
        return;
      }
      try {
        if (existingEntity) {
          const updatePayload: UpdateEntityPayload = {
            id: existingEntity.id,
            name: data.name,
            description: data.description,
            type: data.type,
            config: {
              $case: data.type as any,
              value: data.config?.value ?? {},
            },
          };

          await serverDispatch(updateEntity(updatePayload));
          upsertRef.current?.onUpsert({
            name: data.name,
            id: existingEntity.id,
          });
        } else {
          const refs = entityReferencesAdapter.createInitialState();
          if (stepId) {
            if (stepType === 'default_step') {
              refs.noCodeSteps = {
                ...refs.noCodeSteps,
                [`${flowId}/${stepId}`]: true,
              };
            } else if (stepType === 'advanced_step') {
              refs.flowSteps = {
                ...refs.flowSteps,
                [`${flowId}/${stepId}`]: true,
              };
            }
          }
          const newEntityId = generateEntityId();
          await serverDispatch(
            createEntity({
              id: newEntityId,
              name: data.name,
              description: data.description,
              type: data.type,
              references: refs,
              config: data.config ?? getDefaultEntityConfig(data.type),
            }),
          );
          upsertRef.current?.onUpsert({
            name: data.name,
            id: newEntityId,
          });
          setEntityId(newEntityId);
        }
      } catch {
        toast.error({
          title: existingEntity
            ? ENTITY_SIDEBAR_UI_COPY.error.updateFailed
            : ENTITY_SIDEBAR_UI_COPY.error.createFailed,
        });
        upsertRef.current?.onCancelUpsert();
      }
    },
    [
      serverDispatch,
      flowId,
      stepId,
      existingEntity,
      stepType,
      isOpen,
      toast,
      setEntityId,
      upsertRef,
    ],
  );

  const renderForm = () => {
    const type = methods.getValues('type');
    switch (type) {
      case EntityType.ALPHANUMERIC:
        return <AlphanumericForm readonly={readonly} />;
      case EntityType.NUMERIC:
        return <NumberForm readonly={readonly} />;
      case EntityType.TIME:
        return <TimeForm readonly={readonly} />;
      case EntityType.PHONE_NUMBER:
        return <PhoneNumberForm readonly={readonly} />;
      case EntityType.ENUM:
        return <MultipleChoiceForm readonly={readonly} />;
      default:
        return null;
    }
  };

  const handleAutoSave = useCallback(async () => {
    const values = methods.getValues();
    const isValid = await methods.trigger();
    if (isValid && values.name.trim() && methods.formState.isDirty) {
      await handleCreateOrUpdateEntity(values);
    }
  }, [methods, handleCreateOrUpdateEntity]);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        methods.setFocus('name');
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, methods]);

  return (
    <StickySidebar
      headerOffset={headerOffset}
      id="entity-sidebar"
      isMainNavOpen={isMainNavOpen}
      open={isOpen}
      position="right"
      zIndex={Z_INDEXES.sidebar}
      stickyHeader
    >
      {({
        scrollableRef,
      }: {
        scrollableRef: React.MutableRefObject<HTMLDivElement | null>;
      }) => (
        <Styled.FormWrapper id={ACTION_SIDEBAR_ID}>
          <FormProvider
            defaultValues={defaultValues}
            id="entity-form"
            methods={methods}
            onBlur={handleCreateOrUpdateEntity}
            onSubmit={handleCreateOrUpdateEntity}
          >
            <SidebarScrollableContent ref={scrollableRef} $flex>
              <SidebarStickyHeader>
                <Styled.HeaderContainer>
                  <Styled.HeaderWrapper>
                    <div className="w-full">
                      <FormTextInput
                        // @ts-ignore
                        $fontSize="regular"
                        controlKey="name"
                        data-test-id="entity-name-input"
                        readOnly={!isEditable}
                        role="input"
                        fullWidth
                        isHeadline
                      />
                    </div>
                    <Button
                      Icon={CloseIcon}
                      data-test-id="close-entity-sidebar-btn"
                      label={ENTITY_SIDEBAR_UI_COPY.button.close}
                      size="small"
                      type="button"
                      variant="GHOST"
                      onClick={onClose}
                    />
                  </Styled.HeaderWrapper>
                </Styled.HeaderContainer>
              </SidebarStickyHeader>

              <Styled.Wrapper>
                <Styled.FormContent>
                  <div className="flex flex-col gap-sm3 [&_.help-text]:!whitespace-normal">
                    <FormSelect<EntityFormProps>
                      controlKey="type"
                      data-test-id="entity-type-select"
                      label={ENTITY_SIDEBAR_UI_COPY.form.type.label}
                      options={ENTITY_TYPE_OPTIONS.sort((a, b) =>
                        a.label.localeCompare(b.label),
                      )}
                      placeholder={ENTITY_SIDEBAR_UI_COPY.form.type.placeholder}
                      value={ENTITY_TYPE_OPTIONS.find(
                        option => option.value === methods.watch('type'),
                      )}
                      onBlur={handleAutoSave}
                      onChange={async (
                        selectedOption: {
                          label: string;
                          value: EntityType;
                        } | null,
                      ) => {
                        const value = selectedOption?.value;
                        if (value) {
                          methods.setValue('type', value);
                          methods.setValue(
                            'config',
                            getDefaultEntityConfig(value),
                          );
                        }
                      }}
                    />

                    <FormTextInput<EntityFormProps>
                      controlKey="description"
                      data-test-id="entity-description-input"
                      help={ENTITY_SIDEBAR_UI_COPY.form.description.helpText}
                      label={ENTITY_SIDEBAR_UI_COPY.form.description.label}
                      placeholder={
                        ENTITY_SIDEBAR_UI_COPY.form.description.placeholder
                      }
                      readOnly={!isEditable}
                    />

                    {renderForm()}
                  </div>
                </Styled.FormContent>
              </Styled.Wrapper>
            </SidebarScrollableContent>
          </FormProvider>
        </Styled.FormWrapper>
      )}
    </StickySidebar>
  );
};

export default EntitySidebar;
